import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import HeroSection from './components/HeroSection';
import TabNavigation from './components/TabNavigation';
import VetrinaTab from './components/VetrinaTab';
import PromoTab from './components/PromoTab';
import ProdottiTab from './components/ProdottiTab';
import ServiziTab from './components/ServiziTab';
import RecensioniTab from './components/RecensioniTab';
import gaTrackingService from '../../services/gaTrackingService';
import { analyticsService } from '../../services/analyticsService';
import { vetrineService } from '../../services/vetrineService';
import { vetrineContentService } from '../../services/vetrineContentService';
import { useAuth } from '../../contexts/AuthContext';

const StoreDetails = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const storeId = searchParams?.get('id');
  
  const [activeTab, setActiveTab] = useState('vetrina');
  const [isSaved, setIsSaved] = useState(false);
  const [viewStartTime] = useState(Date.now());
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [ratingBreakdown, setRatingBreakdown] = useState([]);

  // Load store data from database
  useEffect(() => {
    loadStoreData();
  }, [storeId]);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      
      if (storeId) {
        // Load real store from database
        const storeData = await vetrineService?.getStoreById(storeId);
        
        if (storeData) {
          // Transform database store to component format
          const transformedStore = transformDatabaseStore(storeData);
          setStore(transformedStore);
          
          // Load products from new content table
          const dbProducts = await vetrineContentService?.getProductsByStore(storeId);
          const transformedProducts = dbProducts?.map(p => ({
            id: p?.id,
            name: p?.nome,
            category: p?.categoria || 'Prodotti',
            description: p?.descrizione || '',
            price: p?.prezzoScontato || p?.prezzo || 0,
            originalPrice: p?.prezzoScontato ? p?.prezzo : null,
            image: p?.immagine || '/assets/images/no_image.png',
            imageAlt: p?.nome,
            inStock: p?.disponibile ?? true,
            isNew: p?.nuovo ?? false,
            discount: p?.prezzoScontato ? Math.round(((p?.prezzo - p?.prezzoScontato) / p?.prezzo) * 100) : null
          }));
          setProducts(transformedProducts || []);
          
          // Load services from new content table
          const dbServices = await vetrineContentService?.getServicesByStore(storeId);
          const transformedServices = dbServices?.map(s => ({
            id: s?.id,
            name: s?.nome,
            icon: s?.icona || 'Briefcase',
            description: s?.descrizione || '',
            price: s?.prezzo || 0,
            duration: s?.durata || 'Variabile',
            availability: s?.disponibilita || 'Disponibile',
            features: s?.caratteristiche || []
          }));
          setServices(transformedServices || []);
          
          // Load promotions from new content table
          const dbPromotions = await vetrineContentService?.getActivePromotions(storeId);
          const transformedPromotions = dbPromotions?.map(p => ({
            id: p?.id,
            title: p?.titolo,
            description: p?.descrizione,
            discount: p?.valoreSconto ? (p?.tipo === 'sconto_percentuale' ? `${p?.valoreSconto}%` : `€${p?.valoreSconto}`) : '',
            image: p?.immagine || '/assets/images/no_image.png',
            imageAlt: p?.titolo,
            validUntil: p?.dataFine,
            terms: p?.condizioni || ''
          }));
          setPromotions(transformedPromotions || []);
          
          // Load reviews from new content table
          const dbReviews = await vetrineContentService?.getReviewsByStore(storeId);
          const transformedReviews = dbReviews?.map(r => ({
            id: r?.id,
            author: r?.nomeUtente,
            rating: r?.rating,
            date: r?.createdAt,
            title: r?.titolo || '',
            comment: r?.commento,
            verified: r?.verificata ?? false,
            helpful: r?.utileCount || 0,
            storeResponse: r?.rispostaNegozio ? {
              text: r?.rispostaNegozio,
              date: r?.rispostaData
            } : null
          }));
          setReviews(transformedReviews || []);
          
          // Calculate rating breakdown
          const breakdown = [5, 4, 3, 2, 1]?.map(rating => ({
            rating,
            count: dbReviews?.filter(r => r?.rating === rating)?.length || 0,
            percentage: dbReviews?.length > 0 
              ? Math.round((dbReviews?.filter(r => r?.rating === rating)?.length / dbReviews?.length) * 100)
              : 0
          }));
          setRatingBreakdown(breakdown);
        } else {
          // Store not found
          navigate('/not-found');
        }
      } else {
        // No ID provided
        navigate('/main-storefronts');
      }
    } catch (error) {
      console.error('Error loading store:', error);
      navigate('/main-storefronts');
    } finally {
      setLoading(false);
    }
  };

  const transformDatabaseStore = (dbStore) => {
    // Parse gallery images
    let galleryImages = [];
    if (dbStore?.galleryProdotti) {
      try {
        galleryImages = typeof dbStore?.galleryProdotti === 'string' 
          ? JSON.parse(dbStore?.galleryProdotti) 
          : dbStore?.galleryProdotti;
      } catch (e) {
        console.error('Error parsing gallery:', e);
      }
    }

    // Build gallery array
    const gallery = galleryImages?.map((url, index) => ({
      image: url,
      imageAlt: `${dbStore?.nomeNegozio} - Immagine ${index + 1}`
    })) || [];

    // Parse opening hours if available
    let openingHours = [];
    if (dbStore?.orariApertura) {
      // Check if it's structured JSON data (array format)
      if (typeof dbStore?.orariApertura === 'string' && dbStore?.orariApertura?.trim()?.startsWith('[')) {
        try {
          openingHours = JSON.parse(dbStore?.orariApertura);
        } catch (e) {
          console.error('Error parsing hours JSON:', e);
          // If JSON parse fails, treat as plain text
          openingHours = dbStore?.orariApertura;
        }
      } else {
        // Plain text format - use as is
        openingHours = dbStore?.orariApertura;
      }
    }

    return {
      id: dbStore?.id,
      name: dbStore?.nomeNegozio,
      category: dbStore?.categoria,
      categoryIcon: "Store",
      zone: dbStore?.zona,
      rating: dbStore?.rating || 0,
      reviewCount: dbStore?.numeroRecensioni || 0,
      heroImage: dbStore?.immaginePrincipale || '/assets/images/no_image.png',
      heroImageAlt: `${dbStore?.nomeNegozio} - Immagine principale`,
      logo: dbStore?.logoNegozio || '/assets/images/no_image.png',
      logoAlt: `${dbStore?.nomeNegozio} - Logo`,
      badges: dbStore?.tipoVetrina === 'demo' 
        ? [{ type: 'demo', icon: 'Info', label: 'DEMO' }]
        : dbStore?.inEvidenza 
          ? [{ type: 'featured', icon: 'Star', label: 'In Evidenza' }]
          : [],
      description: dbStore?.descrizioneBreve || `Scopri ${dbStore?.nomeNegozio}.`,
      gallery: gallery,
      openingHours: openingHours,
      address: dbStore?.indirizzo || '',
      phone: dbStore?.telefono || '',
      email: dbStore?.email || '',
      website: dbStore?.sitoWeb || '',
      whatsapp: dbStore?.whatsapp || '',
      instagram: dbStore?.instagram || '',
      facebook: dbStore?.facebook || '',
      coordinates: { lat: 43.2983, lng: 13.4532 }, // Default Macerata coords
    };
  };

  const tabs = [
    { id: 'vetrina', label: 'Vetrina', icon: 'Store', count: 0 },
    { id: 'promo', label: 'Promo & Cashback', icon: 'Tag', count: promotions?.length },
    { id: 'prodotti', label: 'Prodotti', icon: 'Package', count: products?.length },
    { id: 'servizi', label: 'Servizi', icon: 'Wrench', count: services?.length },
    { id: 'recensioni', label: 'Recensioni', icon: 'Star', count: reviews?.length }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track store view on mount
  useEffect(() => {
    if (!store?.id) return;
    
    const trackView = async () => {
      try {
        const sessionId = `session_${Date.now()}_${Math.random()}`;
        await analyticsService?.trackStoreView(store?.id, user?.id || null, sessionId);
      } catch (error) {
        console.error('Error tracking store view:', error);
      }

      // Track in GA4
      gaTrackingService?.trackStorefrontView({
        storeId: store?.id,
        storeName: store?.name,
        category: store?.category,
        zone: store?.zone,
        isFeatured: store?.badges?.some((b) => b?.type === 'premium'),
        hasPromotion: promotions?.length > 0
      });
    };

    trackView();

    // Track engagement time on unmount
    return () => {
      const timeSpent = Date.now() - viewStartTime;
      if (timeSpent > 3000) { // Only track if spent more than 3 seconds
        gaTrackingService?.trackEngagementTime({
          storeId: store?.id,
          timeSpent
        });
      }
    };
  }, [store?.id, user?.id, viewStartTime, promotions?.length]);

  // Track tab navigation
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);

    // Track tab navigation in GA4
    gaTrackingService?.trackTabNavigation({
      storeId: store?.id,
      storeName: store?.name,
      tabName
    });
  };

  const handleSave = () => {
    setIsSaved(!isSaved);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: store?.name,
        text: `Scopri ${store?.name} su Vetrine di Macerata`,
        url: window.location?.href
      });
    } else {
      alert('Link copiato negli appunti!');
    }
  };

  const handleDirections = () => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${store?.coordinates?.lat},${store?.coordinates?.lng}`,
      '_blank'
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!store) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{store?.name} - Vetrine di Macerata</title>
        <meta name="description" content={store?.description?.substring(0, 160)} />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onFilterClick={() => {}} />
        <Breadcrumb />

        <main className="pb-20 lg:pb-8">
          <HeroSection
            store={store}
            onSave={handleSave}
            onShare={handleShare}
            onDirections={handleDirections}
            isSaved={isSaved}
          />

          <TabNavigation
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabs={tabs}
          />

          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            {activeTab === 'vetrina' && <VetrinaTab store={store} />}
            {activeTab === 'promo' && (
              <PromoTab
                promotions={promotions}
                cashbackOffers={[]}
              />
            )}
            {activeTab === 'prodotti' && <ProdottiTab products={products} />}
            {activeTab === 'servizi' && <ServiziTab services={services} />}
            {activeTab === 'recensioni' && (
              <RecensioniTab
                reviews={reviews}
                ratingBreakdown={ratingBreakdown}
                averageRating={store?.rating}
                totalReviews={store?.reviewCount}
              />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default StoreDetails;